import { useEffect, useState } from "react";
import { markOwnNavigation } from "./own-navigation.js";
import { parse, toHash, type Route } from "./route.js";

/**
 * The route model, bound to the browser.
 *
 * Navigation goes through the hash rather than through state, so the back button works and a
 * reload lands on the same screen.
 */
function parseHash(): Route {
  return parse(window.location.hash);
}

export function useRoute(): [Route, (next: Route) => void, (next: Route) => void] {
  const [route, setRoute] = useState<Route>(parseHash);

  useEffect(() => {
    const onChange = () => setRoute(parseHash());
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  return [
    route,
    // Navigation goes through the hash rather than through state, so the back button works and a
    // reload lands on the same screen.
    (next: Route) => {
      // See `own-navigation.ts`: this plain hash push also fires a spurious `popstate`, and
      // `back-navigation.ts` needs to know to ignore it.
      markOwnNavigation();
      window.location.hash = toHash(next);
    },
    /**
     * A redirect that must not itself become a history entry — used only for the two guards in
     * `App.tsx` that send a screen somewhere else out from under it (a private route reached
     * signed out, a teacher-only route reached by a child).
     *
     * A plain `navigate` here would leave the ORIGINAL, now-invalid route sitting in history: back
     * from the redirect would return to it, the same guard would fire again, and back would
     * bounce between the two forever. `replaceState` never fires `hashchange`, so the route has to
     * be set directly.
     *
     * Not for the back button. Every screen's own back arrow, and the hardware and browser back
     * keys, go through plain history (`window.history.back()` / `back-navigation.ts`) — this
     * function only ever replaces the CURRENT entry, never answers a press.
     */
    (next: Route) => {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}${toHash(next)}`,
      );
      setRoute(next);
    },
  ];
}
